"use client";

import { useId } from "react";
import { cn } from "@/lib/utils";
import styles from "./PropCafeLogo.module.css";

const sizes = {
  sm: styles.logoSm,
  md: styles.logoMd,
  lg: styles.logoLg,
  sidebar: styles.logoSidebar,
  auth: styles.logoAuth,
} as const;

export type PropCafeLogoSize = keyof typeof sizes;

/**
 * PropCafe logo (house icon + "prop.Cafe" text).
 * House + ".Cafe": copper radial; "prop": investor-aligned primary teal.
 */
export function PropCafeLogo({
  size = "md",
  className,
  priority: _priority,
  iconOnly = false,
  ...props
}: {
  size?: PropCafeLogoSize;
  className?: string;
  priority?: boolean;
  /** When true, show only the house icon (e.g. collapsed sidebar). */
  iconOnly?: boolean;
} & Omit<React.HTMLAttributes<HTMLSpanElement>, "children">) {
  const rawId = useId();
  const gradientId = rawId.replace(/:/g, "-");
  const houseFillUrl = `url(#prop-cafe-house-${gradientId})`;

  return (
    <span
      className={cn(styles.logo, sizes[size], iconOnly && styles.logoIconOnly, className)}
      role="img"
      aria-label="PropCafe"
      {...props}
    >
      <span className={styles.houseWrap}>
        <svg
          viewBox="0 0 200 200"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={styles.houseSvg}
          aria-hidden
        >
          <defs>
            <radialGradient
              id={`prop-cafe-house-${gradientId}`}
              cx="132"
              cy="58"
              r="172"
              fx="124"
              fy="52"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor="#CD7F32" />
              <stop offset="28%" stopColor="#B87333" />
              <stop offset="58%" stopColor="#9a5520" />
              <stop offset="100%" stopColor="#7B3F00" />
            </radialGradient>
          </defs>
          {/* Single outline path (house + chimney) – copper/bronze radial metal */}
          <path
            d="M59 25 L81 25 L81 80 L20 80 L100 20 L180 80 L160 80 L160 180 L40 180 L40 80 L59 50.75 L59 25 Z"
            fill={houseFillUrl}
            fillRule="nonzero"
          />
          <rect x="75" y="100" width="20" height="20" fill="white" />
          <rect x="105" y="100" width="20" height="20" fill="white" />
          <rect x="75" y="130" width="20" height="20" fill="white" />
          <rect x="105" y="130" width="20" height="20" fill="white" />
        </svg>
      </span>
      <span className="inline-flex items-baseline leading-none self-end -ml-0.5 md:-ml-1">
        <span className={styles.textProp}>prop</span>
        <span className={styles.textCafe}>.Cafe</span>
      </span>
    </span>
  );
}
